import React, { useState, useMemo, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import { MenuItem, Select } from '@mui/material'
import themeColors from '@layers-frontend/commons/styles/themeColors'
import { withTranslator } from '../HOCComponents/withTranslator'
import ShareScreenshotButton from '../ToolsComponents/ShareScreenshotButton'
import { isS3Url } from '../../actions/biLabs'
import { trackEvent } from '../analytics/analytics'
import { TRACK_EVENTS } from '../../constants'

// 60px TopBar + the 66px controls row (8px padding above and below a 50px share button). Both
// dashboard variants render that row, so both reserve exactly this much and the iframe below it
// always starts at the same place.
const CONTENT_OFFSET = '126px'

const IframeWrapper = styled.iframe`
  overflow: auto;
  width: 100%;
  height: calc(100% - ${CONTENT_OFFSET});
  justify-content: center;
  align-items: center;
  border: 0;
  padding-top: 16px;
  background: white;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`
// The controls row is app chrome under the TopBar: deep navy with the bars' dark mint line along its
// bottom edge, so it reads apart from the customer's dashboard below.
const SelectorWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  padding: 8px;
  background: ${themeColors.deepblueColor3};
  box-shadow: inset 0 -1px 0 ${themeColors.mintBorder};
  width: 100%;
`

/**
 * Renders a dashboard iframe, resolving `s3://` URLs to a short-lived presigned
 * https URL first. Regular Power BI URLs are embedded directly and unchanged.
 *
 * For S3 URLs the presign is fetched fresh on every url change (they expire in
 * ~60s), and any failure is swallowed: `presignedUrl` stays null and nothing
 * renders, rather than surfacing an error.
 */
const DashboardFrame = ({ url, title, resolveS3Url }) => {
  const needsResolving = isS3Url(url)
  const [presignedUrl, setPresignedUrl] = useState(null)

  // Tracks every dashboard the user lands on: the initial one and each selector
  // change. Always reports the raw url — for s3 dashboards the presigned https
  // URL is short-lived and carries an access signature, so it must never be
  // sent to analytics.
  useEffect(() => {
    trackEvent(TRACK_EVENTS.BILABS_DASHBOARD_VIEWED, {
      url,
      type: needsResolving ? 's3_html' : 'link',
      title
    })
  }, [url, needsResolving, title])

  useEffect(() => {
    if (!needsResolving || typeof resolveS3Url !== 'function') {
      setPresignedUrl(null)
      return undefined
    }

    let isActive = true
    setPresignedUrl(null)

    Promise.resolve(resolveS3Url(url))
      .then(resolved => {
        if (isActive) setPresignedUrl(typeof resolved === 'string' ? resolved : null)
      })
      .catch(() => {
        if (isActive) setPresignedUrl(null)
      })

    return () => {
      isActive = false
    }
  }, [url, needsResolving, resolveS3Url])

  // Regular Power BI URLs embed directly; s3:// URLs wait for the presigned URL
  // and render nothing until (and unless) it resolves.
  const src = needsResolving ? presignedUrl : url

  if (!src) return null

  return <IframeWrapper src={src} title={title} />
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Parses a semicolon-separated string into cleaned segments
 * @param {string} url - Raw URL string with semicolons
 * @returns {string[]} Array of trimmed, non-empty segments
 */
const parseUrlSegments = url => {
  if (!url) return []
  return url
    .split(';')
    .map(segment => segment.trim())
    .filter(Boolean)
}

/**
 * Converts segments into dashboard objects with title-URL pairs
 * @param {string[]} segments - Array of alternating titles and URLs
 * @returns {Object[]} Array of dashboard objects {title, url}
 */
const parseDashboards = segments => {
  const dashboards = []

  for (let i = 0; i < segments.length; i += 2) {
    const hasTitle = i + 1 < segments.length

    if (hasTitle) {
      dashboards.push({
        title: segments[i],
        url: segments[i + 1]
      })
    } else {
      // Odd number of segments - last one is URL without title
      dashboards.push({
        title: `Dashboard ${dashboards.length + 1}`,
        url: segments[i]
      })
    }
  }

  return dashboards
}

// ============================================================================
// Sub-components
// ============================================================================

// A tile like the dashboard's layout toggle: deep navy, dark mint rim and a mint icon; it fills mint
// under the pointer.
const StyledShareButton = styled.button`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  border: 1px solid ${themeColors.mintBorder};
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 22px;
  color: ${themeColors.mintColor};
  background: ${themeColors.deepblueColor3};
  transition: background-color 0.15s, border-color 0.15s, color 0.15s;
  &:hover {
    background: ${themeColors.mintColor};
    border-color: ${themeColors.mintColor};
    color: ${themeColors.deepblueColor3};
  }
  & i {
    margin: 0;
    padding: 0;
  }
`

const CustomShareButton = ({ t, onClick }) => {
  return (
    <StyledShareButton title={t('Share screenshot')} aria-label={t('Share screenshot')} onClick={onClick} data-testid="share-screenshot-button-custom">
      <i className="fas fa-share-nodes" />
    </StyledShareButton>
  )
}

/**
 * Static twin of the `Select` control: same box, same size, no dropdown. It names the dashboard
 * when there is only one, so the single-dashboard row reads like the multi-dashboard one.
 */
const DashboardTitle = styled.div`
  display: flex;
  align-items: center;
  min-width: 250px;
  height: 48px;
  padding: 14px 20px;
  font-size: 16px;
  color: ${themeColors.whiteColor};
  border: 1px solid ${themeColors.mintBorder};
  border-radius: 12px;
`

/**
 * Renders a single dashboard with the same chrome row as the multi-dashboard variant, showing the
 * dashboard's title where the selector would be.
 *
 * The row is what keeps both variants consistent: it reserves a band of the app's own chrome under
 * the TopBar, so the share button — and the globally-mounted floating LAYI widget, which is fixed
 * at `right: 8px; top: 72px` — land on that band instead of on the customer's dashboard. Before,
 * this variant had no row and floated the share button over the iframe at `z-index: 1000`.
 */
const SingleDashboard = ({ url, title, resolveS3Url, t }) => (
  <Container>
    <SelectorWrapper>
      <DashboardTitle>{t(title)}</DashboardTitle>
      <ShareScreenshotButton CustomButton={CustomShareButton} />
    </SelectorWrapper>

    <DashboardFrame url={url} title={title} resolveS3Url={resolveS3Url} />
  </Container>
)

/**
 * Renders the dashboard selector dropdown: a dark mint contour that turns mint under the pointer and
 * while open. Its list is the theme's navy dropdown, like every other select in the app.
 */
const DashboardSelector = ({ dashboards, selectedIndex, onSelect, t }) => {
  return (
    <div style={{ display: 'flex' }}>
      <SelectorWrapper>
        <label
          htmlFor="dashboard-select"
          style={{
            fontSize: '18px',
            fontWeight: 400,
            color: themeColors.whiteTransparent08,
            marginBottom: '0'
          }}
        >
          {t('Select')}
        </label>
        <Select
          id="dashboard-select"
          value={selectedIndex}
          onChange={e => onSelect(e.target.value)}
          sx={{
            minWidth: '250px',
            height: '48px',
            fontSize: '16px',
            color: themeColors.whiteColor,
            border: `1px solid ${themeColors.mintBorder}`,
            borderRadius: '12px',
            transition: 'border-color 0.15s',
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none'
            },
            '&:hover, &.Mui-focused': {
              borderColor: themeColors.mintColor
            },
            '& .MuiSelect-select': {
              padding: '14px 20px'
            },
            '& .MuiSelect-icon': {
              color: themeColors.whiteTransparent08
            },
            '& .MuiSelect-iconOpen': {
              color: themeColors.mintColor
            }
          }}
        >
          {dashboards.map((dashboard, index) => (
            <MenuItem key={`${dashboard.title}-${index}`} value={index}>
              {t(dashboard.title)}
            </MenuItem>
          ))}
        </Select>
        <ShareScreenshotButton CustomButton={CustomShareButton} />
      </SelectorWrapper>
    </div>
  )
}

/**
 * Renders multiple dashboards with selector
 */
const MultipleDashboards = ({ dashboards, selectedIndex, onIndexChange, resolveS3Url, t }) => {
  const currentDashboard = dashboards[selectedIndex]

  return (
    <Container>
      <DashboardSelector dashboards={dashboards} selectedIndex={selectedIndex} onSelect={onIndexChange} t={t} />
      <DashboardFrame url={currentDashboard.url} title={currentDashboard.title} resolveS3Url={resolveS3Url} />
    </Container>
  )
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * PowerBI component that displays Power BI dashboards in an iframe
 * Supports multiple dashboards via semicolon-separated title;url pairs
 *
 * @param {string} url - Single URL or semicolon-separated format: "title1;url1;title2;url2;..."
 * @param {string} title - Fallback title to describe the embedded content for a11y
 * @param {function} resolveS3Url - Thunk exchanging an `s3://` URL for a presigned https URL
 * @param {function} t - Translation function from withTranslator HOC
 * @returns {JSX.Element|null} The embedded Power BI dashboard(s) with optional selector
 */
function PowerBI({ url, title, resolveS3Url, t }) {
  const [selectedIndex, setSelectedIndex] = useState(0)

  const segments = useMemo(() => parseUrlSegments(url), [url])

  const dashboards = useMemo(() => {
    if (segments.length <= 2) return []
    return parseDashboards(segments)
  }, [segments])

  const handleSelectDashboard = useCallback(index => {
    setSelectedIndex(index)
  }, [])

  // Early return if no URL provided
  if (!url || segments.length === 0) {
    return null
  }

  // Single URL without title - use fallback title prop
  if (segments.length === 1) {
    return <SingleDashboard url={segments[0]} title={title} resolveS3Url={resolveS3Url} t={t} />
  }

  // Single title;URL pair - render the configured title instead of a selector
  if (segments.length === 2) {
    return <SingleDashboard url={segments[1]} title={segments[0]} resolveS3Url={resolveS3Url} t={t} />
  }

  // Multiple dashboards - render with selector
  return <MultipleDashboards dashboards={dashboards} selectedIndex={selectedIndex} onIndexChange={handleSelectDashboard} resolveS3Url={resolveS3Url} t={t} />
}

export default withTranslator(PowerBI)
